import { API_BASE_URL } from "../../constant/constant";
import React, { useState, useRef, useEffect } from "react";
import { io } from "socket.io-client";
import { Box, CircularProgress, MenuItem, Tooltip } from "@mui/material";
import Popover from "@mui/material/Popover";
import Typography from "@mui/material/Typography";
import { Header, Heading } from "../Summarize/Style";
import { useOutletContext, useNavigate } from "react-router-dom";
import ChatMessage from "./ChatMessage";
import SuggestionMessages from "./SuggestionMessages";
import SkillsSection from "./SkillsSection";
import {
  AvatarSection,
  AssistantName,
  AssistantTitle,
  ChatBox,
  ChatDiv,
  InputContainer,
  Input,
  FileDiv,
  EditImg,
  UploadDiv,
  Container,
  Content,
  NotificationSection,
  TopNavbarSection,
  AddDocument,
  RightNavbarSection,
  SendMessageIcon,
  RightNavItem,
  BelowNavbarSection,
  InnerContainer,
} from "./style";
import PaperClip from "../../assets/paper-clip.svg";
import FileCard from "../Cards/FileCard";
import PdfIcon from "../../assets/pdf-large.svg";
import DocxIcon from "../../assets/docx-large.svg";
import TxtIcon from "../../assets/txt-large.svg";
import { handleFileUploadWithSignedURL } from "../Environment/Asconfig";
import { AVATARS } from "../Constants/Constants";
import ComputerIcon from "../../assets/computer-icon.svg";
import NotificationWhiteIcon from "../../assets/notification_white_icon.svg";
import AvatarChatHomeIcon from "../../assets/avatar_chat_home_icon.svg";
import AvatarChatCameraIcon from "../../assets/avatar_chat_camera_icon.svg";
import AvatarChatBelowIcon from "../../assets/avatar_chat_below_icon.svg";
import PlusIconWhite from "../../assets/plus_icon_white.svg";
import { TopNavItem } from "./style";
import { toast } from "react-toastify";

import { useTranslation } from "react-i18next";

const uploadFileToS3 = async (file) => {
  try {
    const response = await handleFileUploadWithSignedURL(file);
    return response.fileUrl;
  } catch (error) {
    toast.error(error);
    return null;
  }
};

const formatTimestamp = (date) => {
  const offset = -date.getTimezoneOffset();
  const offsetSign = offset >= 0 ? "+" : "-";
  const offsetHours = String(Math.floor(Math.abs(offset) / 60)).padStart(
    2,
    "0"
  );
  const offsetMinutes = String(Math.abs(offset) % 60).padStart(2, "0");

  return `${date
    .toISOString()
    .slice(0, 19)}${offsetSign}${offsetHours}:${offsetMinutes}`;
};

const getFileTypeFromName = (fileName) => {
  const extension = fileName.split(".").pop().toLowerCase();
  if (extension === "pdf") return "pdf";
  if (["doc", "docx"].includes(extension)) return "DOCX";
  if (extension === "txt") return "TXT";
  return "unknown";
};

const formatFileFromUrl = (url) => {
  const fileName = decodeURIComponent(url.split("/").pop());
  return {
    name: fileName,
    type: getFileTypeFromName(fileName),
    url: url,
  };
};



const SkillChat = ({ uploadedFiles, messages = [], setMessages, avatarData, isChatting }) => {
  const bottomRef = useRef(null);
  const [isChatHistoryLoading, setIsChatHistoryLoading] = useState(false);
  const [initialLoadComplete, setInitialLoadComplete] = useState(false);
  const { t } = useTranslation();


  const fetchChatHistory = async () => {
    try {
      const userId = localStorage.getItem("id");
      const token = localStorage.getItem("token");
      setIsChatHistoryLoading(true)
      const response = await fetch(
        `${API_BASE_URL}/api/auth/skills-chat?user_id=${userId}`,
        {
          headers: {
            "x-auth-token": token,
          },
        }
      );

      if (response.status === 429) {
        toast.error(response.message);
      }

      const data = await response.json();

      if (data.responseCode === 200) {
        const formattedMessages = data.skillsChats.flatMap((chat) => [
          {
            role: "human",
            content: chat.query,
            files: chat.files?.map((fileUrl) => formatFileFromUrl(fileUrl)),
            userTimestamp: chat.userTimestamp,
            isGreetingMsg: chat.isGreetingMsg,
          },
          {
            role: "ai",
            content: chat.content,
            avatarTimestamp: chat.avatarTimestamp,
            isGreetingMsg: chat.isGreetingMsg,
            ...(chat.is_dynamic && {
              is_dynamic: chat.is_dynamic,
              workflow_data: chat.workflow_data
            })
          },
        ]);
        const formattedDisplayMessages = formattedMessages.map((msg) => ({
          id:
            msg.role === "human"
              ? msg.content + "-query"
              : msg.content + "-response",
          text: msg.content,
          isUser: msg.role === "human",
          files: msg.role === "human" ? msg.files : [],
          userTimestamp:
            msg.role === "human" ? new Date(parseInt(msg.userTimestamp)) : null,
          avatarTimestamp:
            msg.role === "ai" ? new Date(parseInt(msg.avatarTimestamp)) : null,
          isGreetingMsg: msg.isGreetingMsg,
          ...(msg.role === "ai" && msg.is_dynamic && { is_dynamic: msg.is_dynamic, workflow_data: msg.workflow_data })

        }));
        return formattedDisplayMessages;
      }
      return [];
    } catch (error) {
      return []
    } finally {
      setIsChatHistoryLoading(false)
    }
  };
  useEffect(() => {
    if (messages.length === 0 && !isChatting) {
      fetchChatHistory().then((fetchedMessages) => {
        console.log("Fetched chat history:", fetchedMessages);
        setMessages(fetchedMessages);
        setIsChatHistoryLoading(false);
        setInitialLoadComplete(true);
      });
    }
  }, []);

  // Separate useEffect for handling scrolling
  useEffect(() => {
    if (bottomRef.current && (messages.length > 0 || initialLoadComplete)) {
      bottomRef.current.scrollIntoView({
        behavior: initialLoadComplete ? "smooth" : "auto",
      });
    }
  }, [messages, initialLoadComplete]);



  const renderChatContent = () => {
    if (isChatHistoryLoading) {
      return (
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "100%",
          }}
        >
          <CircularProgress />
        </div>
      );
    }

    return (
      <ChatDiv>
        {messages.map((message, index) => (
          <ChatMessage
            key={index}
            text={message.text}
            isUser={message.isUser}
            avatarImage={
              avatarData
                ? AVATARS.find(
                  (avatar) => avatar.imageId === avatarData.imageId
                )?.src
                : null
            }
            avatarData={avatarData?.avatarName}
            files={message?.files || []}
            fileUrl={message?.fileurl}
            userColor="#202226"
            userTextColor="#FFFFFF"
            avatarColor="#FFF"
            avatarTextColor="#202226"
            userTimestamp={message?.userTimestamp}
            avatarTimestamp={message?.avatarTimestamp}
          />
        ))}
        <div ref={bottomRef} />

      </ChatDiv>
    );
  };

  return (
    <Content
      style={{
        height:
          uploadedFiles.length > 0
            ? "calc(100vh - 322px)"
            : "calc(100vh - 252px)",
        // overflowY: "auto"
      }}
    >
      {
        messages.length === 0 && !isChatHistoryLoading ? (
          <div style={{ textAlign: "center", color: "#666", marginTop: "2rem" }}>
            {t("avatarChat.noMessages")}
          </div>
        ) : (
          <ChatBox>
            {renderChatContent()}
          </ChatBox>
        )
      }


    </Content>

  );
};

export default SkillChat;